import React, { useState, useEffect } from 'react';
import { Row, Col, Switch, TimePicker, ConfigProvider, DatePicker } from 'antd';
import { Button } from '../../../components';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Colors } from '../../../config';
import Calender from '../../../components/Calender';
import BasicText from '../../../components/Text/BasicText';
import BasicDrawer from '../../../components/Drawer';
import Textarea from '../../../components/TextField/TextArea';
import moment from 'moment';
import dayjs from 'dayjs';
import useColors from '../../../hooks/useColors';
import { useSelector, useDispatch } from 'react-redux';
import DashboardActionBar from '../../../components/DashboardActionBar';
import { AppAction } from '../../../store/actions';
import { Utils } from '../../../config';

const groupByMonth = (prefs, sortDir = 'asc') => {
    const groups = {};
    prefs.forEach((p) => {
        const key = moment(p.date).format('MMMM YYYY');
        if (!groups[key]) groups[key] = [];
        groups[key].push(p);
    });
    return Object.keys(groups)
        .sort((a, b) => {
            const diff = moment(a, 'MMMM YYYY').diff(moment(b, 'MMMM YYYY'));
            return sortDir === 'asc' ? diff : -diff;
        })
        .map((month) => ({ month, items: groups[month] }));
};

const MyAvailability = () => {
    const { colorPrimary } = useColors();
    const dispatch = useDispatch();
    const { availability } = useSelector((state) => state.AppReducer);
    const availabilityLoader = useSelector((state) => state.AppReducer?.loader);
    const timeFormat = useSelector((state) => state.AppReducer?.timeFormat) || 'hh:mm A';
    const use12Hours = timeFormat === 'hh:mm A';
    const timePickerFormat = use12Hours ? ['hh:mm A', 'hh:mm a'] : ['HH:mm', 'HHmm'];

    const [formKey, setFormKey] = useState(0);

    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            preferences: [{ dates: [], startTime: null, endTime: null, note: '' }],
            no_overnight: false,
        },
    });

    useEffect(() => {
        dispatch(AppAction.GetAvailability());
    }, []);

    useEffect(() => {
        if (availability?.data?.no_overnight !== undefined) {
            reset({
                preferences: [{ dates: [], startTime: null, endTime: null, note: '' }],
                no_overnight: availability.data.no_overnight,
            });
        }
    }, [availability, reset]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
    const [selectedDayDetails, setSelectedDayDetails] = useState(null);
    const [showPreviously, setShowPreviously] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [editIsAvailable, setEditIsAvailable] = useState(true);
    const [editNoOvernight, setEditNoOvernight] = useState(false);
    const [editStartTime, setEditStartTime] = useState(null);
    const [editEndTime, setEditEndTime] = useState(null);
    const [editNote, setEditNote] = useState('');

    const { fields } = useFieldArray({ control, name: 'preferences' });

    // Split preferences into three groups
    const allPreferences = availability?.data?.preferences || [];
    const today = moment();
    const currentMonthStart = today.clone().startOf('month');
    const currentMonthEnd = today.clone().endOf('month');

    const previousPrefs = allPreferences
        .filter((p) => moment(p.date).isBefore(currentMonthStart))
        .sort((a, b) => moment(b.date).diff(moment(a.date)));

    const currentMonthPrefs = allPreferences.filter((p) => {
        const d = moment(p.date);
        return !d.isBefore(currentMonthStart) && !d.isAfter(currentMonthEnd);
    });

    const upcomingPrefs = allPreferences
        .filter((p) => moment(p.date).isAfter(currentMonthEnd))
        .sort((a, b) => moment(a.date).diff(moment(b.date)));

    const openDetailsDrawer = (preference) => {
        setSelectedDayDetails(preference);
        setEditMode(false);
        setEditIsAvailable(preference?.is_available ?? true);
        setEditNoOvernight(preference?.no_overnight ?? false);
        setEditStartTime(
            preference?.time_ranges?.[0]?.start
                ? dayjs(preference.time_ranges[0].start, 'HH:mm')
                : null
        );
        setEditEndTime(
            preference?.time_ranges?.[0]?.end
                ? dayjs(preference.time_ranges[0].end, 'HH:mm')
                : null
        );
        setEditNote(preference?.note || '');
        setDetailsDrawerOpen(true);
    };

    const DatecellRender = (current) => {
        const cellDate = current.format('YYYY-MM-DD');
        const preference = currentMonthPrefs.find(
            (p) => moment(p.date).format('YYYY-MM-DD') === cellDate
        );
        if (!preference) return null;
        return (
            <li
                className="p-2 text-sm flex flex-col gap-1 availability-cell group"
                style={{ background: colorPrimary, color: Colors.White }}
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        openDetailsDrawer(preference);
                    }}
                    className="mt-1 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    style={{ background: Colors.White, color: colorPrimary }}
                >
                    View Details
                </button>
            </li>
        );
    };

    const FormHandler = (data) => {
        const newDates = [];
        data.preferences.forEach((pref) => {
            pref.dates?.forEach((date) => {
                const formatted = date?.format ? date.format('YYYY-MM-DD') : date;
                if (formatted) newDates.push(formatted);
            });
        });

        if (newDates.length === 0) return;

        const existingDates = allPreferences.map((p) =>
            moment(p.date).format('YYYY-MM-DD')
        );
        const duplicate = newDates.find((d) => existingDates.includes(d));
        if (duplicate) {
            Utils.showMessage(
                'warning',
                `A preference already exists for ${moment(duplicate).format('DD MMM YYYY')}. Edit or delete the existing one first.`
            );
            return;
        }

        const formattedPreferences = [];
        data.preferences.forEach((pref) => {
            if (pref.dates && pref.dates.length > 0) {
                pref.dates.forEach((date) => {
                    const formattedDate = date?.format ? date.format('YYYY-MM-DD') : date;
                    const timeRange = [];
                    if (pref.startTime && pref.endTime) {
                        const start = pref.startTime.format
                            ? pref.startTime.format('HH:mm')
                            : pref.startTime;
                        const end = pref.endTime.format
                            ? pref.endTime.format('HH:mm')
                            : pref.endTime;
                        timeRange.push({ start, end });
                    }
                    formattedPreferences.push({
                        date: formattedDate,
                        is_available: true,
                        time_range: timeRange,
                        note: pref.note,
                    });
                });
            }
        });

        dispatch(
            AppAction.AddAvailability(
                { no_overnight: data.no_overnight, preferences: formattedPreferences },
                () => {
                    setDrawerOpen(false);
                    reset({
                        preferences: [{ dates: [], startTime: null, endTime: null, note: '' }],
                        no_overnight: false,
                    });
                    dispatch(AppAction.GetAvailability());
                }
            )
        );
    };

    const DrawerCloseHandler = () => {
        setDrawerOpen(false);
        reset({
            preferences: [{ dates: [], startTime: null, endTime: null, note: '' }],
            no_overnight: false,
        });
    };

    const handleDeletePreference = () => {
        if (!selectedDayDetails?.date) return;
        const formattedDate = moment(selectedDayDetails.date).format('YYYY-MM-DD');
        dispatch(
            AppAction.DeleteAvailability({ date: formattedDate }, () => {
                setDetailsDrawerOpen(false);
                setSelectedDayDetails(null);
                dispatch(AppAction.GetAvailability());
            })
        );
    };

    const handleSaveEdit = () => {
        if (!selectedDayDetails?.date) return;
        const time_ranges = [];
        if (editStartTime && editEndTime) {
            time_ranges.push({
                start: editStartTime.format('HH:mm'),
                end: editEndTime.format('HH:mm'),
            });
        }
        const formattedDate = moment(selectedDayDetails.date).format('YYYY-MM-DD');
        dispatch(
            AppAction.UpdateAvailability(
                {
                    date: formattedDate,
                    is_available: editIsAvailable,
                    time_ranges,
                    no_overnight: editNoOvernight,
                },
                () => {
                    setEditMode(false);
                    setDetailsDrawerOpen(false);
                    dispatch(AppAction.GetAvailability());
                }
            )
        );
    };

    const AvailabilityCard = ({ pref }) => (
        <div
            className="w-full flex items-center justify-between px-4 py-3 rounded-md"
            style={{ background: Colors.White }}
        >
            <div className="flex flex-col gap-1">
                <BasicText
                    text={moment(pref.date).format('DD MMM YYYY')}
                    className="text-sm font-semibold"
                />
                <BasicText
                    text={pref.is_available ? 'Available' : 'Not Available'}
                    className="text-xs"
                    style={{ color: pref.is_available ? colorPrimary : Colors.Error }}
                />
            </div>
            <div className="flex items-center gap-4">
                {pref.time_ranges?.length > 0 && (
                    <BasicText
                        text={`${pref.time_ranges[0].start} – ${pref.time_ranges[0].end}`}
                        className="text-sm"
                        style={{ color: Colors.LabelColor }}
                    />
                )}
                <Button.Basic
                    text="View Details"
                    onClick={() => openDetailsDrawer(pref)}
                    className="h-8 px-3 border-none text-white text-xs"
                    style={{ backgroundColor: colorPrimary, fontSize: '12px' }}
                />
            </div>
        </div>
    );

    return (
        <>
            <DashboardActionBar text='My Availability' />

            <Row className="w-full flex flex-col gap-6">
                {/* Header */}
                <Row
                    className="w-full py-6 px-3 flex items-center justify-between rounded-md"
                    style={{ background: Colors.White }}
                >
                    <BasicText
                        text={`My Availability for ${today.format('DD MMM YYYY')}`}
                        className="text-base font-semibold"
                    />
                    <Button.Basic
                        onClick={() => {
                            reset({
                                preferences: [{ dates: [], startTime: null, endTime: null, note: '' }],
                                no_overnight: false,
                            });
                            setFormKey((k) => k + 1);
                            setDrawerOpen(true);
                        }}
                        style={{
                            width: 'fit-content',
                            height: '40px',
                            color: Colors.White,
                            background: colorPrimary,
                        }}
                        className="h-11 outline-none border-0 rounded-sm px-4"
                        text="+ Add Preference"
                    />
                </Row>

                {/* Previously */}
                {previousPrefs.length > 0 && (
                    <div className="w-full">
                        <div
                            className="px-4 py-3 rounded-md mb-3 flex items-center justify-between cursor-pointer"
                            style={{ background: Colors.White }}
                            onClick={() => setShowPreviously((v) => !v)}
                        >
                            <BasicText text="Previously" className="text-base font-semibold" />
                            <span
                                className="text-xs px-3 py-1 rounded-full font-semibold select-none"
                                style={{
                                    background: showPreviously ? colorPrimary : Colors.LightGray,
                                    color: showPreviously ? Colors.White : Colors.LabelColor,
                                    transition: 'all 0.2s',
                                }}
                            >
                                {showPreviously ? 'Hide' : `Show (${previousPrefs.length})`}
                            </span>
                        </div>
                        {showPreviously && groupByMonth(previousPrefs, 'desc').map(({ month, items }) => (
                            <div key={month} className="mb-4">
                                <BasicText
                                    text={month}
                                    className="text-sm font-semibold mb-2 px-1"
                                    style={{ color: Colors.LabelColor }}
                                />
                                <div className="flex flex-col gap-2">
                                    {items.map((pref, idx) => (
                                        <AvailabilityCard key={idx} pref={pref} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Current month calendar */}
                <div className="w-full">
                    <div
                        className="px-4 py-3 rounded-md mb-3"
                        style={{ background: Colors.White }}
                    >
                        <BasicText
                            text={today.format('MMMM YYYY')}
                            className="text-base font-semibold"
                        />
                    </div>
                    <Calender headerRender={() => ''} cellRender={DatecellRender} />
                </div>

                {/* Upcoming */}
                {upcomingPrefs.length > 0 && (
                    <div className="w-full">
                        <div
                            className="px-4 py-3 rounded-md mb-3"
                            style={{ background: Colors.White }}
                        >
                            <BasicText text="Upcoming" className="text-base font-semibold" />
                        </div>
                        {groupByMonth(upcomingPrefs, 'asc').map(({ month, items }) => (
                            <div key={month} className="mb-4">
                                <BasicText
                                    text={month}
                                    className="text-sm font-semibold mb-2 px-1"
                                    style={{ color: Colors.LabelColor }}
                                />
                                <div className="flex flex-col gap-2">
                                    {items.map((pref, idx) => (
                                        <AvailabilityCard key={idx} pref={pref} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Row>

            {/* Add Preference Drawer */}
            <BasicDrawer
                title="Add Availability Preferences"
                openDrawer={drawerOpen}
                width="600"
                onClose={DrawerCloseHandler}
                children={
                    <form key={formKey} className="w-full" onSubmit={handleSubmit(FormHandler)}>
                        <Row className="w-full flex items-center justify-between mb-4 px-1">
                            <Controller
                                name="no_overnight"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={value}
                                            onChange={onChange}
                                            className="custom-switch"
                                            style={{ '--myColor': colorPrimary }}
                                        />
                                        <BasicText
                                            text={value ? 'Overnight' : 'No Overnight'}
                                            className="font-semibold"
                                        />
                                    </div>
                                )}
                            />
                        </Row>

                        <div
                            style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}
                            className="px-1"
                        >
                            {fields.map((item, index) => (
                                <div key={item.id} className="mb-6 pb-4 relative">
                                    <Row className="w-full flex items-center justify-between">
                                        <Col lg={24}>
                                            <Controller
                                                name={`preferences.${index}.dates`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <div className="flex flex-col mb-4">
                                                        <label
                                                            style={{ color: Colors.LabelColor }}
                                                            className="font-normal mb-1"
                                                        >
                                                            Select Dates
                                                        </label>
                                                        <DatePicker
                                                            multiple
                                                            value={value}
                                                            onChange={onChange}
                                                            format="DD-MM-YYYY"
                                                            className="outline-none py-2 rounded-md w-full"
                                                            disabledDate={(current) =>
                                                                current.isBefore(moment().subtract(1, 'day'))
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            />
                                        </Col>

                                        <Col lg={11}>
                                            <Controller
                                                name={`preferences.${index}.startTime`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <div className="flex flex-col">
                                                        <label
                                                            style={{ color: Colors.LabelColor }}
                                                            className="font-normal"
                                                        >
                                                            Start Time
                                                        </label>
                                                        <ConfigProvider
                                                            theme={{
                                                                components: {
                                                                    DatePicker: {
                                                                        optionSelectedBg: colorPrimary,
                                                                        optionActiveBg: Colors.LightGray,
                                                                        optionSelectedColor: Colors.White,
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <TimePicker
                                                                value={value || null}
                                                                minuteStep={15}
                                                                format={timePickerFormat}
                                                                use12Hours={use12Hours}
                                                                onChange={onChange}
                                                                className="outline-none py-2 rounded-md h-9 w-full"
                                                            />
                                                        </ConfigProvider>
                                                    </div>
                                                )}
                                            />
                                        </Col>

                                        <Col lg={11}>
                                            <Controller
                                                name={`preferences.${index}.endTime`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <div className="flex flex-col">
                                                        <label
                                                            style={{ color: Colors.LabelColor }}
                                                            className="font-normal"
                                                        >
                                                            End Time
                                                        </label>
                                                        <ConfigProvider
                                                            theme={{
                                                                components: {
                                                                    DatePicker: {
                                                                        optionSelectedBg: colorPrimary,
                                                                        optionActiveBg: Colors.LightGray,
                                                                        optionSelectedColor: Colors.White,
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <TimePicker
                                                                value={value || null}
                                                                minuteStep={15}
                                                                format={timePickerFormat}
                                                                use12Hours={use12Hours}
                                                                onChange={onChange}
                                                                className="outline-none py-2 rounded-md h-9 w-full"
                                                            />
                                                        </ConfigProvider>
                                                    </div>
                                                )}
                                            />
                                        </Col>

                                        <Col lg={24}>
                                            <Controller
                                                name={`preferences.${index}.note`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Textarea
                                                        className="w-full"
                                                        labeltext="Notes"
                                                        rows={2}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            ))}
                        </div>

                        <div className="w-full flex justify-end items-center mt-4">
                            <input
                                type="submit"
                                style={{ background: colorPrimary, color: Colors.White }}
                                className="cursor-pointer h-10 rounded-sm px-12 border-0"
                                value="Save Preferences"
                            />
                        </div>
                    </form>
                }
            />

            {/* View Details / Edit Drawer */}
            <BasicDrawer
                title="Day Preference Details"
                openDrawer={detailsDrawerOpen}
                width="500"
                onClose={() => {
                    setDetailsDrawerOpen(false);
                    setEditMode(false);
                }}
                children={
                    selectedDayDetails ? (
                        <div className="p-4">
                            <div className="mb-4 flex items-center justify-between">
                                <BasicText
                                    text={`Date: ${moment(selectedDayDetails.date).format('DD MMM YYYY')}`}
                                    className="text-base font-semibold"
                                />
                                <div className="flex gap-2">
                                    {!editMode && (
                                        <>
                                            <Button.Basic
                                                text="Edit"
                                                onClick={() => setEditMode(true)}
                                                className="h-8 px-4 border-none text-white border-transparent"
                                                style={{ backgroundColor: colorPrimary }}
                                            />
                                            <Button.Basic
                                                text="Delete"
                                                onClick={handleDeletePreference}
                                                loader={availabilityLoader}
                                                className="h-8 px-4 border-none text-white border-transparent"
                                                style={{ backgroundColor: Colors.Error }}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <BasicText
                                    text="Availability:"
                                    className="text-sm font-semibold mb-1"
                                    style={{ color: Colors.LabelColor }}
                                />
                                <BasicText
                                    text={selectedDayDetails.is_available ? 'Available' : 'Not Available'}
                                    className="text-base"
                                />
                            </div>

                            {editMode ? (
                                <>
                                    <Row className="w-full flex items-center justify-between mb-4 px-1">
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={editIsAvailable}
                                                onChange={setEditIsAvailable}
                                                className="custom-switch"
                                                style={{ '--myColor': colorPrimary }}
                                            />
                                            <BasicText
                                                text={editIsAvailable ? 'Available' : 'Not Available'}
                                                className="font-semibold"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={editNoOvernight}
                                                onChange={setEditNoOvernight}
                                                className="custom-switch"
                                                style={{ '--myColor': colorPrimary }}
                                            />
                                            <BasicText
                                                text={editNoOvernight ? 'Overnight' : 'No Overnight'}
                                                className="font-semibold"
                                            />
                                        </div>
                                    </Row>

                                    <Row className="w-full flex items-center justify-between mb-4">
                                        <Col lg={11}>
                                            <label
                                                style={{ color: Colors.LabelColor }}
                                                className="font-normal block mb-1"
                                            >
                                                Start Time
                                            </label>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        DatePicker: {
                                                            optionSelectedBg: colorPrimary,
                                                            optionActiveBg: Colors.LightGray,
                                                            optionSelectedColor: Colors.White,
                                                        },
                                                    },
                                                }}
                                            >
                                                <TimePicker
                                                    value={editStartTime}
                                                    minuteStep={15}
                                                    format={timePickerFormat}
                                                    use12Hours={use12Hours}
                                                    onChange={setEditStartTime}
                                                    className="outline-none py-2 rounded-md h-9 w-full"
                                                />
                                            </ConfigProvider>
                                        </Col>

                                        <Col lg={11}>
                                            <label
                                                style={{ color: Colors.LabelColor }}
                                                className="font-normal block mb-1"
                                            >
                                                End Time
                                            </label>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        DatePicker: {
                                                            optionSelectedBg: colorPrimary,
                                                            optionActiveBg: Colors.LightGray,
                                                            optionSelectedColor: Colors.White,
                                                        },
                                                    },
                                                }}
                                            >
                                                <TimePicker
                                                    value={editEndTime}
                                                    minuteStep={15}
                                                    format={timePickerFormat}
                                                    use12Hours={use12Hours}
                                                    onChange={setEditEndTime}
                                                    className="outline-none py-2 rounded-md h-9 w-full"
                                                />
                                            </ConfigProvider>
                                        </Col>

                                        <Col lg={24}>
                                            <Textarea
                                                className="w-full"
                                                labeltext="Notes"
                                                rows={2}
                                                value={editNote}
                                                onChange={(e) => setEditNote(e.target.value)}
                                            />
                                        </Col>
                                    </Row>

                                    <div className="flex gap-3 justify-end mt-4">
                                        <Button.Basic
                                            text="Cancel"
                                            onClick={() => setEditMode(false)}
                                            className="h-9 px-5 border rounded"
                                            style={{
                                                background: 'transparent',
                                                color: colorPrimary,
                                                border: `1px solid ${colorPrimary}`,
                                            }}
                                        />
                                        <Button.Basic
                                            text="Save"
                                            onClick={handleSaveEdit}
                                            loader={availabilityLoader}
                                            className="h-9 px-5 border-none text-white"
                                            style={{ backgroundColor: colorPrimary }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {selectedDayDetails.time_ranges?.length > 0 ? (
                                        <div className="mb-4">
                                            <BasicText
                                                text="Time Range(s):"
                                                className="text-sm font-semibold mb-2"
                                                style={{ color: Colors.LabelColor }}
                                            />
                                            {selectedDayDetails.time_ranges.map((range, idx) => (
                                                <div
                                                    key={idx}
                                                    className="mb-2 p-3 rounded"
                                                    style={{ background: Colors.LightGray }}
                                                >
                                                    <BasicText
                                                        text={`${range.start} - ${range.end}`}
                                                        className="text-base"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mb-4">
                                            <BasicText
                                                text="Time Range:"
                                                className="text-sm font-semibold mb-1"
                                                style={{ color: Colors.LabelColor }}
                                            />
                                            <BasicText
                                                text="No time range specified"
                                                className="text-base text-gray-500"
                                            />
                                        </div>
                                    )}

                                    {selectedDayDetails.note && (
                                        <div className="mb-4">
                                            <BasicText
                                                text="Notes:"
                                                className="text-sm font-semibold mb-1"
                                                style={{ color: Colors.LabelColor }}
                                            />
                                            <div
                                                className="p-3 rounded"
                                                style={{ background: Colors.LightGray }}
                                            >
                                                <BasicText
                                                    text={selectedDayDetails.note}
                                                    className="text-base"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="p-4">
                            <BasicText text="No details available" className="text-base text-gray-500" />
                        </div>
                    )
                }
            />
        </>
    );
};

export default MyAvailability;
